// src/utils/orderDisplayUtils.js
/**
 * Utility functions for displaying order numbers consistently across toolkits
 * Matches the logic used in PackersToolkit and ProductionToolkit
 */

/**
 * Strip leading # from order number - ensures we never double-prefix when displaying
 * @param {string|number} value - Order number value
 * @returns {string} Value with any leading # removed
 */
export function stripOrderNumberHash(value) {
    return String(value ?? '').trim().replace(/^#+/, '');
}

/**
 * Normalize order number for display (handles UUIDs, numeric IDs, etc.)
 * @param {string|number} value - Order number value
 * @returns {string} Normalized order number
 */
export function normalizeOrderNumberForDisplay(value) {
    const withoutHash = stripOrderNumberHash(value);
    if (!withoutHash) return '';

    // If it's an OrderDesk UUID-like ID, shorten to first 8 chars
    if (/[a-f0-9]{8}-[a-f0-9]{4}-/i.test(withoutHash)) {
        return withoutHash.slice(0, 8);
    }

    // If it's numeric (typical OrderDesk order_number), keep full value
    if (/^\d+$/.test(withoutHash)) {
        return withoutHash;
    }

    // Otherwise cap to 12 chars to avoid huge pills
    return withoutHash.length > 12 ? withoutHash.slice(0, 12) : withoutHash;
}

/**
 * Get order display label (formatted order number)
 * Prioritizes internal order number from checkout_data over OrderDesk generated numbers
 * @param {Object} order - Order object
 * @returns {string} Formatted order number (e.g., "#12345")
 */
export function getOrderDisplayLabel(order) {
    // Priority: orderNumber (internal) > orderId (CSV row[0], should be internal) > order_number (might be OrderDesk)
    // Don't use source_id or OrderDesk id - those are not the internal order numbers
    const preferred = order?.orderNumber || order?.orderId;
    const short = normalizeOrderNumberForDisplay(preferred);
    const fallback = stripOrderNumberHash(order?.orderId);
    return short ? `#${short}` : (fallback ? `#${fallback}` : '—');
}

/**
 * Get order ID for internal use (matching logic from PackersToolkit)
 * Uses orderId field, with fallback to orderNumber/order_number
 * @param {Object} order - Order object
 * @returns {string} Order ID for matching/lookup
 */
export function getOrderId(order) {
    return order?.orderId || order?.orderNumber || order?.order_number || '';
}
